/**
 * Disputes hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useDisputesRelayQuery as UseDisputesRelayQuery,
  useDisputesRelayQuery$variables,
  DisputeFilterInput,
  DisputeSortInput,
} from './__generated__/useDisputesRelayQuery.graphql';
import type { useDisputesRelayDirectQuery as UseDisputesRelayDirectQuery } from './__generated__/useDisputesRelayDirectQuery.graphql';
import type { useDisputesRelayByIdQuery } from './__generated__/useDisputesRelayByIdQuery.graphql';
import type { useDisputesRelayPaginationFragment$key } from './__generated__/useDisputesRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const DisputesListPaginationFragment = graphql`
  fragment useDisputesRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useDisputesRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "DisputeFilterInput" }
    sort: { type: "[DisputeSortInput!]" }
  ) {
    listDisputes(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useDisputesRelayPaginationFragment_listDisputes"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          amount
          fee
          net
          chargeId
          stripeDisputeId
          status
          reason
          isOpen
          evidenceDueBy

          createdOn
          systemId
          organizationId
          charge {
            id
            amount
            status
          }
          user {
            id
            firstName
            lastName
            email
            phone
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const DisputesListQuery = graphql`
  query useDisputesRelayQuery(
    $first: Int
    $after: String
    $filter: DisputeFilterInput
    $sort: [DisputeSortInput!]
  ) {
    ...useDisputesRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

const DisputesListDirectQuery = graphql`
  query useDisputesRelayDirectQuery(
    $first: Int
    $after: String
    $filter: DisputeFilterInput
    $sort: [DisputeSortInput!]
  ) {
    listDisputes(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          amount
          fee
          net
          chargeId
          stripeDisputeId
          status
          reason
          isOpen
          evidenceDueBy

          createdOn
          systemId
          organizationId
          charge {
            id
            amount
            status
          }
          user {
            id
            firstName
            lastName
            email
            phone
          }
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const DisputeByIdQuery = graphql`
  query useDisputesRelayByIdQuery($id: ID!) {
    getDispute(id: $id) {
      id
      amount
      fee
      net
      chargeId
      stripeDisputeId
      status
      reason
      isOpen
      evidenceDueBy

      createdOn
      systemId
      organizationId
      charge {
        id
        amount
        status
        description
        paymentIntentId
      }
      user {
        id
        firstName
        lastName
        email
        phone
      }
      system {
        id
        oldId
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { DisputeFilterInput, DisputeSortInput };

export interface DisputeUserGraphQL {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: number | null;
}

export interface DisputeChargeGraphQL {
  id: string;
  amount?: number | null;
  status?: string | null;
  description?: string | null;
  paymentIntentId?: string | null;
}

export interface DisputeSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface DisputeGraphQL {
  id: string;
  amount: number;
  fee?: number | null;
  net?: number | null;
  chargeId: string;
  stripeDisputeId: string;
  status: string;
  reason?: string | null;
  isOpen?: boolean | null;
  evidenceDueBy?: string | null;
  createdOn: string;
  systemId?: string | null;
  organizationId?: string | null;
  charge?: DisputeChargeGraphQL | null;
  user?: DisputeUserGraphQL | null;
  system?: DisputeSystemGraphQL | null;
}

export interface DisputeQueryFilter {
  systemId?: string;
  organizationId?: string;
  status?: string;
  isOpen?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching disputes list
 */
export function useDisputesRelay(
  filter?: DisputeQueryFilter,
  options?: {
    limit?: number;
    sort?: DisputeSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useDisputesRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  const queryData = useLazyLoadQuery<UseDisputesRelayQuery>(
    DisputesListQuery,
    variables
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseDisputesRelayQuery,
    useDisputesRelayPaginationFragment$key
  >(DisputesListPaginationFragment, queryData);

  const nodes =
    data.listDisputes?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as DisputeGraphQL[],
    count: data.listDisputes?.count,
    pageInfo: data.listDisputes?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Suspense-based hook for fetching a single dispute by ID
 */
export function useDisputeByIdRelay(disputeId: string) {
  const data = useLazyLoadQuery<useDisputesRelayByIdQuery>(DisputeByIdQuery, {
    id: disputeId,
  });

  return data.getDispute as DisputeGraphQL | null;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for disputes data
 */
export function useDisputesRelayQuery(
  filter?: DisputeQueryFilter,
  options?: {
    limit?: number;
    sort?: DisputeSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<DisputeGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () =>
      options?.sort ?? [
        { field: 'CREATED_ON' as const, order: 'DESC' as const },
      ],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useDisputesRelayQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseDisputesRelayDirectQuery>(
          relayEnvironment,
          DisputesListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listDisputes?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as DisputeGraphQL[]);
          setCount(result.listDisputes?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch disputes')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single dispute by ID with explicit loading state
 */
export function useDisputeByIdRelayQuery(
  disputeId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<DisputeGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(disputeId);

  useEffect(() => {
    if (!enabled || !disputeId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useDisputesRelayByIdQuery>(
          relayEnvironment,
          DisputeByIdQuery,
          { id: disputeId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getDispute as DisputeGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch dispute')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, disputeId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(
  filter?: DisputeQueryFilter
): DisputeFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: DisputeFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.status) {
    relayFilter.status = { eq: filter.status };
  }
  if (filter.isOpen !== undefined) {
    relayFilter.isOpen = { eq: filter.isOpen };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
